/**
 * @file 单机会话（localSession）—— 客户端本地跑完整局。
 *
 * 用 `@majiang/core` 公共 API 驱动：`createGame` / `applyAction` /
 * `getLegalActions` / `projectStateForSeat`；三个对手用 M2 的 `decide`。
 * 客户端在此模式下是**宿主**，合法持有完整 `GameState`（含 `rng`），
 * 但渲染层仍然只拿到 `projectStateForSeat` 的投影 —— 信息隔离红线不破。
 *
 * ## 驱动方式
 * 每次引擎推进后 `pump()`：找到下一个需要行动的 AI 座位 → `decide` 出动作 →
 * 按拟人化延迟 `setTimeout` 后 `apply`。轮到人类座位时停下来等 UI。
 * 人类动作由 `getLegalActions` 解析出权威的 core Action 再提交（杜绝越权）。
 */

import type {
  Action,
  AIAction,
  AIObservation,
  AIPhase,
  GameState,
  SeatIndex,
  SeatSetup,
  TileKind,
} from '@majiang/core';
import {
  applyAction,
  createGame,
  createRuleSnapshot,
  decide,
  getLegalActions,
  projectStateForSeat,
} from '@majiang/core';
import type { ClientViewState, LocalSettings, ReplaySnapshot, SessionCallbacks, SessionController, TableUiState, UIAction } from './types';
import { deriveUi } from './ui';

const HUMAN_SEAT = 0 as SeatIndex;
const AI_SEATS: readonly SeatIndex[] = [1, 2, 3];

/** 牌实例 id → 27 维种类序号（0..26）。core 未公开 keyOfKind，本地按公开映射推导。 */
function tileKeyOf(id: number): number {
  return Math.floor(id / 4);
}

/** 花色 → 单字母记法（川麻简写）。 */
const SUIT_ABBR: Record<string, string> = { wan: 'm', tong: 'p', tiao: 's' };
/** 花色 → 中文。 */
const SUIT_CN: Record<string, string> = { wan: '万', tong: '筒', tiao: '条' };
/** 副露类型 → 中文。 */
const MELD_CN: Record<string, string> = { peng: '碰', gang_ming: '明杠', gang_an: '暗杠', gang_bu: '补杠' };

/** 手牌 id 列表 → 川麻紧凑记法，如 "123m 55p 999s"（按花色分组，同点合并）。 */
function handNotation(ids: readonly number[]): string {
  const counts = new Array<number>(27).fill(0);
  for (const id of ids) {
    const k = tileKeyOf(id);
    counts[k] = (counts[k] ?? 0) + 1;
  }
  const parts: string[] = [];
  const suits: readonly string[] = ['wan', 'tong', 'tiao'];
  for (let s = 0; s < 3; s++) {
    let run = '';
    for (let r = 0; r < 9; r++) {
      const c = counts[s * 9 + r] ?? 0;
      if (c === 0) continue;
      run += String(r + 1).repeat(c);
    }
    if (run.length > 0) parts.push(`${run}${SUIT_ABBR[suits[s]!]!}`);
  }
  return parts.join(' ');
}

/** TileKind → "4筒" 中文记法。 */
function kindCn(kind: TileKind | null): string {
  if (kind === null) return '?';
  const suit = kind.slice(0, -1);
  const rank = kind.slice(-1);
  return `${rank}${SUIT_CN[suit] ?? ''}`;
}

/** 由权威态直接构造客户端视图状态（单机模式不走事件规约器）。 */
function buildClientState(state: GameState, seat: SeatIndex): ClientViewState {
  const view = projectStateForSeat(state, seat);
  const pc = state.pendingClaims;
  const claimWindow = pc === null ? null : { fromSeat: pc.fromSeat, tileId: pc.tileId, trigger: pc.trigger, deadlineAt: pc.deadlineAt };
  const opt = pc?.options.find((o) => o.seat === seat);
  const myClaim =
    opt === undefined || pc === null
      ? null
      : { tileId: pc.tileId, fromSeat: pc.fromSeat, trigger: pc.trigger, actions: opt.actions, gangKinds: opt.gangKinds, deadlineAt: pc.deadlineAt };
  const selfTingKinds = view.self.isTing ? (view.self.tingCandidates?.map((t) => t.kind) ?? null) : null;
  return { view, claimWindow, myClaim, selfTingKinds, needsResync: false };
}

/** 从合法动作集里解析出与 UI 动作匹配的权威 core Action。 */
function resolveCoreAction(action: UIAction, legal: readonly Action[]): Action | null {
  switch (action.type) {
    case 'declare_void':
      return legal.find((a) => a.type === 'declare_void' && a.suit === action.suit) ?? null;
    case 'discard':
      return legal.find((a) => a.type === 'discard' && a.tileId === action.tileId) ?? null;
    case 'peng':
      return legal.find((a) => a.type === 'peng') ?? null;
    case 'gang':
      return legal.find((a) => a.type === 'gang' && a.kind === action.kind) ?? null;
    case 'hu':
      return legal.find((a) => a.type === 'hu') ?? null;
    case 'pass':
      return legal.find((a) => a.type === 'pass') ?? null;
  }
}

/** 把 M2 AI 决策映射为合法 core Action。 */
function resolveAiAction(ai: AIAction, legal: readonly Action[]): Action | null {
  switch (ai.type) {
    case 'DECIDE_QUE':
      return legal.find((a) => a.type === 'declare_void' && a.suit === ai.queSuit) ?? null;
    case 'DISCARD': {
      const target = ai.tile;
      return legal.find((a) => a.type === 'discard' && tileKeyOf(a.tileId) === target) ?? null;
    }
    case 'PENG':
      return legal.find((a) => a.type === 'peng') ?? null;
    case 'GANG':
      return legal.find((a) => a.type === 'gang' && a.kind === ai.kind) ?? null;
    case 'HU_ZIMO':
    case 'HU_DIANPAO':
    case 'HU_QIANGGANG':
      return legal.find((a) => a.type === 'hu') ?? null;
    case 'PASS':
      return legal.find((a) => a.type === 'pass') ?? null;
  }
}

/** 构造 AI 决策观测（口径与 apps/server room-do.ts 一致，只消费投影）。 */
function buildAIObservation(state: GameState, seat: SeatIndex): AIObservation {
  const view = projectStateForSeat(state, seat);
  const hand = new Uint8Array(27);
  for (const id of view.self.hand) {
    hand[tileKeyOf(id)] = (hand[tileKeyOf(id)] ?? 0) + 1;
  }
  const pc = state.pendingClaims;
  let phase: AIPhase;
  if (state.phase === 'void') phase = 'DECIDE_QUE';
  else if (pc !== null) phase = pc.trigger === 'gang_bu' ? 'QIANG_GANG_WINDOW' : 'RESPONSE_WINDOW';
  else phase = 'SELF_ACTION';

  const opponents = view.opponents.map((o) => {
    const queSuitIdx = o.voidSuit === null ? -1 : o.voidSuit === 'wan' ? 0 : o.voidSuit === 'tong' ? 1 : 2;
    const hasClearedQue =
      o.voidSuit === null ? false : o.discards.some((id) => Math.floor(tileKeyOf(id) / 9) === queSuitIdx);
    return {
      seat: o.seat,
      queSuit: o.voidSuit,
      melds: o.melds.map((m) => ({ type: m.type, kind: m.kind })),
      discards: o.discards.map((id) => tileKeyOf(id)),
      handSize: o.handCount,
      status: (o.hasWon ? 'WON' : 'PLAYING') as 'WON' | 'PLAYING',
      hasClearedQue,
    };
  });

  return {
    seat,
    hand,
    melds: view.self.melds.map((m) => ({ type: m.type, kind: m.kind })),
    queSuit: view.self.voidSuit,
    phase,
    opponents,
    remaining: view.wallRemaining,
    turnIndex: view.turnCount,
    ownDiscards: view.self.discards.map((id) => tileKeyOf(id)),
    lastDiscard:
      state.lastDiscard !== null
        ? { seat: state.lastDiscard.seat, tileKey: tileKeyOf(state.lastDiscard.tileId) }
        : null,
  };
}

export class LocalSession implements SessionController {
  readonly mode = 'local' as const;
  readonly seat: SeatIndex | null = HUMAN_SEAT;
  private readonly settings: LocalSettings;
  private readonly cb: SessionCallbacks;
  private state: GameState | null = null;
  private aiTimer: number | null = null;
  private disposed = false;
  /** 多局制当前局号（1 起）。 */
  private round = 1;

  constructor(settings: LocalSettings, cb: SessionCallbacks) {
    this.settings = settings;
    this.cb = cb;
  }

  /** 开局（可重复调用开启新局；round 用于复盘快照标号）。 */
  async start(round = 1): Promise<void> {
    this.round = round;
    const rules = createRuleSnapshot({ ai: { difficulty: this.settings.aiDifficulty } });
    const seed = crypto.getRandomValues(new Uint32Array(1))[0] ?? Math.floor(Math.random() * 0xffffffff);
    const seats: readonly [SeatSetup, SeatSetup, SeatSetup, SeatSetup] = [
      { playerId: `local:${Math.floor(Math.random() * 1e9)}`, isRobot: false },
      { playerId: 'bot:1', isRobot: true },
      { playerId: 'bot:2', isRobot: true },
      { playerId: 'bot:3', isRobot: true },
    ];
    const result = createGame({
      gameId: `local-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
      seed,
      rules,
      seats,
      dealer: HUMAN_SEAT,
      now: Date.now(),
    });
    this.state = result.state;
    this.notifyView();
    this.pump();
  }

  send(action: UIAction): void {
    if (this.disposed || this.state === null) return;
    const legal = getLegalActions(this.state, HUMAN_SEAT);
    const core = resolveCoreAction(action, legal);
    if (core === null) {
      this.cb.onToast('当前不能执行该操作');
      return;
    }
    this.apply(core);
  }

  resync(): void {
    // 单机模式本地即权威，无需 resync
  }

  dispose(): void {
    this.disposed = true;
    if (this.aiTimer !== null) window.clearTimeout(this.aiTimer);
    this.aiTimer = null;
    this.state = null;
  }

  /* ─────────────── 引擎推进 ─────────────── */

  private apply(action: Action): void {
    if (this.disposed || this.state === null) return;
    const result = applyAction(this.state, action, { now: Date.now() });
    const rejected = result.events.some((ev) => ev.type === 'action_rejected');
    if (!rejected) {
      this.state = result.state;
    } else {
      const reason = result.events.find((ev) => ev.type === 'action_rejected')?.payload.reason;
      this.cb.onToast(`操作被拒绝：${String(reason ?? 'unknown')}`);
    }
    this.notifyView();
    // 摸牌动画（自家摸到）
    for (const ev of result.events) {
      if (ev.type === 'drawn' && ev.audience.scope === 'seat' && ev.audience.seat === HUMAN_SEAT) {
        this.cb.onDraw?.(ev.payload.tileId as number);
      }
    }
    this.pump();
  }

  /** 找下一个需要行动的 AI 座位；人类回合 / 无待办返回 null。 */
  private nextAISeat(state: GameState): SeatIndex | null {
    if (state.phase === 'void') {
      for (const s of AI_SEATS) {
        if (state.players[s].voidSuit === null) return s;
      }
      return null;
    }
    if (state.phase !== 'playing') return null;
    const pc = state.pendingClaims;
    if (pc !== null) {
      for (const opt of pc.options) {
        if (opt.seat === HUMAN_SEAT) continue; // 人类候选：等 UI
        if (pc.responses[opt.seat] === undefined) return opt.seat;
      }
      return null;
    }
    const turn = state.turn;
    if (turn !== HUMAN_SEAT && !state.players[turn].hasWon) return turn;
    return null;
  }

  /** 驱动 AI：找到待办座位 → decide → 延迟后 apply。 */
  private pump(): void {
    if (this.disposed || this.state === null) return;
    const state = this.state;
    if (state.phase === 'settlement' || state.phase === 'finished') {
      // 复盘快照（单机宿主合法持有完整 state）：四家手牌 + 副露 + 净分
      const snapshot: ReplaySnapshot = {
        round: this.round,
        hands: ([0, 1, 2, 3] as const).map((seat) => {
          const p = state.players[seat];
          const net = state.settlement?.netScores[seat] ?? 0;
          return {
            seat,
            playerId: p.playerId,
            handNotation: handNotation(p.hand),
            meldNotation: p.melds.map((m) => `${MELD_CN[m.type] ?? m.type} ${m.kind === null ? '' : kindCn(m.kind)}`.trim()),
            netScore: net,
          };
        }),
      };
      this.cb.onReplay?.(snapshot);
      this.cb.onEnd(state.phase === 'settlement' ? 'settlement' : 'finished');
      return;
    }
    const seat = this.nextAISeat(state);
    if (seat === null) return; // 等人类操作

    const obs = buildAIObservation(state, seat);
    const difficulty = state.rules.config.ai.difficulty;
    const decision = decide(obs, difficulty, state.rng);
    // AI 决策消费了 PRNG：必须用返回值替换，保持确定性流
    this.state = { ...state, rng: decision.rng };
    const legal = getLegalActions(this.state, seat);
    const core = resolveAiAction(decision.action, legal);
    if (core === null) {
      // 防御：AI 给出非法动作（理论不可达）。回退到 pass / 首张合法弃牌，避免死循环。
      const fallback = legal.find((a) => a.type === 'pass') ?? legal.find((a) => a.type === 'discard') ?? legal[0];
      if (fallback === undefined) {
        this.cb.onToast(`AI·${seat} 无合法动作，牌局暂停`);
        return;
      }
      this.apply(fallback);
      return;
    }
    const delay = decision.thinkDelayMs;
    this.aiTimer = window.setTimeout(() => {
      if (!this.disposed) this.apply(core);
    }, delay);
  }

  private notifyView(): void {
    if (this.state === null) return;
    const clientState = buildClientState(this.state, HUMAN_SEAT);
    const ui: TableUiState = deriveUi(clientState);
    this.cb.onView(clientState, ui);
  }
}
